import React, { useEffect, useRef, useState } from "react";

import { useGetFootprintingFarmPrimaryDataById, useMutateFootprintingFarmPrimaryData, useTranslation } from "utils";

import {
  ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION,
  ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE,
  FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION,
  FARM_FOOTPRINTING_OVERALL_SOIL_TYPE,
} from "models";

import { Card, Flex, Form, FormInstance, Icon, KlimRadioGroup, LazyLoadImage, Option, PageSpin, Select, Tooltip } from "components";

interface FarmGeneralProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

export const FarmSoilForm = ({ primaryDataId, readOnly, farmId, year }: FarmGeneralProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);

  const [overallSoilType, setOverallSoilType] = useState<ENUM_FARM_FOOTPRINTING_OVERALL_SOIL_TYPE | null>(null);
  const [averageSoilSaturation, setAverageSoilSaturation] = useState<ENUM_FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION | null>(null);

  const farmPrimaryDataQuery = useGetFootprintingFarmPrimaryDataById(primaryDataId ?? "");

  const { mutate: mutateFarmPrimaryData } = useMutateFootprintingFarmPrimaryData(
    farmId,
    year,
    {
      overallSoilType,
      averageSoilSaturation,
      farmId,
      year,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !farmPrimaryDataQuery.isLoading) {
        mutateFarmPrimaryData();
      }
    },
    [farmPrimaryDataQuery.isLoading, mutateFarmPrimaryData, readOnly]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (farmPrimaryDataQuery.data && farmPrimaryDataQuery.isSuccess) {
      setOverallSoilType(farmPrimaryDataQuery.data.overallSoilType ?? null);
      setAverageSoilSaturation(farmPrimaryDataQuery.data.averageSoilSaturation ?? null);

      formRef.current?.setFieldsValue({
        overallSoilType: farmPrimaryDataQuery.data.overallSoilType ?? null,
        averageSoilSaturation: farmPrimaryDataQuery.data.averageSoilSaturation ?? null,
      });
    }
  }, [farmPrimaryDataQuery.data, farmPrimaryDataQuery.isSuccess]);

  if (primaryDataId && farmPrimaryDataQuery.isLoading) return <PageSpin />;

  const selectGroupName = "footprinting-farm-soil-select-soil-type";

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-400 padding-right-400 margin-left-100 margin-right-100">{t("soilForm.title")}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <LazyLoadImage alt={"/images/onboarding/farm.jpg"} aria-label="farm-logo-image" src={"/images/onboarding/farm.jpg"} />
        </Flex>
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("soilForm.info")}</p>
        </Flex>
      </Card>
      <Card dataTestId="footprinting-farm-soil-form" className="margin-bottom-100 padding-200">
        <Form ref={formRef}>
          {/* @ts-expect-error need to convert to tsx */}
          <Select
            className="margin-bottom-100"
            disabled={readOnly}
            dataTestId={selectGroupName}
            label={t("soilForm.soilTypeLabel")}
            tooltip={t("soilForm.soilTypeLabelTooltip")}
            rules={[{ required: true, message: false }]}
            initialValue={overallSoilType}
            value={overallSoilType}
            onChange={(v) => setOverallSoilType(v)}
            dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
          >
            {Object.values(FARM_FOOTPRINTING_OVERALL_SOIL_TYPE).map((type) => (
              <Option
                disabled={readOnly}
                key={`${selectGroupName}-${type}`}
                data-testid={`${selectGroupName}-${type}`}
                label={t(`soil.soilTypeOptions.${type}`)}
                value={type}
              >
                {t(`soilForm.soilTypeOptions.${type}`)}
              </Option>
            ))}
          </Select>

          <Flex align="center" gap={16} justify="space-between">
            <span style={{ maxWidth: "60%" }}>
              {t("soilForm.soilSaturationLabel")}
              {true && <span className="required-asterisk">*</span>}
              <Tooltip title={t("soilForm.soilSaturationLabelTooltip")}>
                <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
              </Tooltip>
            </span>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-farm-soil-saturation-radio"
              onChange={({ target }) => {
                setAverageSoilSaturation(target.value);
              }}
              value={averageSoilSaturation}
              optionType="button"
              options={[FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION.HIGH, FARM_FOOTPRINTING_AVERAGE_SOIL_SATURATION.LOW].map((saturation) => ({
                label: t(`soilForm.soilSaturationOptions.${saturation}`),
                value: saturation,
              }))}
            />
          </Flex>
        </Form>
      </Card>
    </div>
  );
};
